import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useMutation } from "@apollo/client";
import { useHistory } from "react-router-dom";
import Swal from "sweetalert2";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import axios from "axios";
import BASE_URL from "../../utils/api/baseUrl";
import {
  CREATE_USER_CERTIFICATE,
  UPDATE_USER_CERTIFICATE,
} from "@/GraphQL/Mutation";
import imageUrl from "@/utils/api/imageUrl";

export default function CreateCertificate({ match }) {
  const history = useHistory();
  const certificateData = history?.location?.state?.certificate;
  const isUpdate = Boolean(certificateData);
  const userId = match?.params?.id;

  const {
    handleSubmit,
    register,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: certificateData?.name || "",
    },
  });

  const [selectedFile, setSelectedFile] = useState(null);

  const [createUserCertificate, { loading }] = useMutation(
    CREATE_USER_CERTIFICATE
  );
  const [updateCertificate, { loading: updateLoading }] = useMutation(
    UPDATE_USER_CERTIFICATE
  );

  useEffect(() => {
    if (certificateData?.name) {
      setValue("name", certificateData.name);
    }
  }, [certificateData, setValue]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    console.log("🚀 ~ handleFileChange ~ file:", file);
    setSelectedFile(file);
  };

  const onSubmit = async (formData) => {
    try {
      let uploadedFile = certificateData?.certificate;

      if (selectedFile) {
        const fileData = new FormData();
        fileData.append("folderName", "certificates");
        fileData.append("files", selectedFile);

        const res = await axios.post(
          `${BASE_URL}/api/image/document-upload`,
          fileData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );

        uploadedFile = res?.data?.data?.[0];
        if (!uploadedFile) throw new Error("File upload failed.");
      }

      if (isUpdate) {
        await updateCertificate({
          variables: {
            id: certificateData.id,
            name: formData.name,
            certificate: uploadedFile,
          },
        });
        Swal.fire("Updated!", "Certificate has been updated.", "success");
      } else {
        if (!selectedFile) {
          Swal.fire("Error", "Please upload a certificate file.", "error");
          return;
        }

        await createUserCertificate({
          variables: {
            user_id: Number(userId),
            name: formData.name,
            certificate: uploadedFile,
          },
        });
        Swal.fire("Success", "Certificate uploaded!", "success");
      }

      reset();
      setSelectedFile(null);
      history.goBack();
    } catch (err) {
      console.error("Submit failed:", err);
      Swal.fire("Error", "Something went wrong!", "error");
    }
  };

  return (
    <div className="container d-flex justify-content-center">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="p-4 card mt-4 w-100"
        style={{ maxWidth: "450px" }}
      >
        <h4 className="fw-bold mb-4 text-center">
          {isUpdate ? "📄 Update Certificate" : "📄 Upload Certificate"}
        </h4>

        {/* Certificate Name */}
        <div className="mb-3">
          <label className="form-label fw-semibold">Certificate Name</label>
          <input
            className="form-control"
            {...register("name", { required: "Name is required" })}
            placeholder="e.g. React Certification"
          />
          {errors.name && (
            <small className="text-danger">{errors.name.message}</small>
          )}
        </div>

        {/* File Upload */}
        <div className="mb-3">
          <label className="form-label fw-semibold">Upload Document</label>
          <div className="border rounded p-3 text-center bg-light">
            <input
              type="file"
              accept=".pdf, .jpg, .jpeg, .png"
              onChange={handleFileChange}
              id="file-upload"
              style={{ display: "none" }}
            />
            <label htmlFor="file-upload" style={{ cursor: "pointer" }}>
              <UploadFileIcon style={{ fontSize: 40, color: "#000" }} />
              <div className="mt-2 text-muted">
                {selectedFile
                  ? selectedFile.name
                  : certificateData?.certificate || "Click to upload"}
              </div>
            </label>

            {/* File Preview */}
            {(selectedFile || certificateData?.certificate) && (
              <div className="mt-3 text-start">
                {selectedFile?.type === "application/pdf" ||
                certificateData?.certificate?.toLowerCase().endsWith(".pdf") ? (
                  <embed
                    src={
                      selectedFile
                        ? URL.createObjectURL(selectedFile)
                        : `${imageUrl}/certificates/${certificateData?.certificate}`
                    }
                    type="application/pdf"
                    width="100%"
                    height="200px"
                    style={{ border: "1px solid #ccc", borderRadius: "4px" }}
                  />
                ) : (
                  <img
                    src={
                      selectedFile
                        ? URL.createObjectURL(selectedFile)
                        : `${imageUrl}/certificates/${certificateData?.certificate}`
                    }
                    alt="Preview"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "200px",
                      objectFit: "contain",
                      borderRadius: "4px",
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        {/* Buttons */}
        <div className="d-flex justify-content-end gap-2 mt-3">
          <button
            className="btn btn-danger"
            type="button"
            onClick={() => history.goBack()}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            type="submit"
            disabled={loading || updateLoading}
          >
            {isUpdate
              ? updateLoading
                ? "Updating..."
                : "Update"
              : loading
              ? "Submitting..."
              : "Submit"}
          </button>
        </div>
      </form>
    </div>
  );
}
